import { Access } from "@p0security/shared/types/access/index";

/** The organization roles P0 can grant, from least to most privileged. An
 * organization's custom roles can't be requested, and someone who holds one
 * can't request at all. */
export const DockerRoles = ["member", "editor", "owner"] as const;
export type DockerRole = (typeof DockerRoles)[number];

export const DockerAccesses = {
  role: Access({
    delegation: {},
    description: "A role in a Docker Hub organization",
    // Filled in after approval, from a fresh lookup. Someone in the
    // organization by then gets the first three, and someone P0 will invite
    // gets `invitedEmail`.
    generated: {
      dockerUsername: {
        description: "The member whose role P0 changes",
        label: "Docker username",
        optional: true,
        type: "string",
      },
      // The member's Docker uuid. A username can be given up and claimed by
      // someone else, so the connector checks the account behind it.
      dockerUserId: { label: "Docker user ID", optional: true, type: "string" },
      // A custom-role holder can't request, so it's a role P0 can grant.
      priorRole: {
        choices: DockerRoles,
        description: "The role restored when this access ends",
        label: "Prior role",
        optional: true,
        type: "string",
      },
      invitedEmail: {
        description:
          "Where P0 sends the organization invite. When this access ends, P0 cancels the invite, or removes the invited person from the organization if they accepted it.",
        label: "Invited email",
        optional: true,
        type: "string",
      },
    },
    label: "Organization role",
    maturity: "beta",
    parameters: {
      // Chosen from the installed organizations, which the request form
      // lists in full. The backend's `newDockerCommands` selects a lone one.
      organization: {
        description: "Docker Hub organization",
        label: "Organization",
        type: "static",
      },
      role: {
        choices: DockerRoles,
        description: "Organization role to grant",
        label: "Role",
        type: "static",
      },
      // Looked up when the request is made, so that the approver sees whether
      // the requestor is a member, and so that the lookup after approval can
      // tell if a member's account changed in between. That later lookup
      // decides between a role change and an invite.
      username: {
        description:
          "The requestor's Docker username in the organization when they asked. Absent for someone who wasn't a member: P0 invites them, unless they've joined by the time the request is approved.",
        label: "Docker username when requested",
        optional: true,
        type: "generated",
      },
    },
    render: { locator: "organization", policy: "role" } as const,
    // Docker Hub gives a member one role per organization
    singleValued: { parameter: "role", per: "organization" },
  }),
};

export type DockerAccesses = typeof DockerAccesses;
export type DockerRoleAccess = DockerAccesses["role"];
